import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { Eyebrow, FadeUp, MaskedLine } from "@/components/motion";

const Lounges = () => {
  const [events, setEvents] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api
      .get("/events")
      .then((res) => setEvents(res.data))
      .catch(() => setFailed(true));
  }, []);

  return (
    <div data-testid="lounges-page">
      <section className="lamp-glow mx-auto max-w-7xl px-5 pb-14 pt-36 sm:px-8 sm:pt-44">
        <Eyebrow suit="♦">Dates for the diary</Eyebrow>
        <h1 className="mt-4 font-serif text-5xl leading-[1.02] tracking-tight text-ivory sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.1}>Upcoming</MaskedLine>
          <MaskedLine delay={0.25} className="italic text-brass">
            Lounges.
          </MaskedLine>
        </h1>
        <FadeUp delay={0.4}>
          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-ivory/70">
            Every game is announced here first. Venue details stay private — they're shared only
            with confirmed Players shortly before each gathering.
          </p>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-5xl px-5 pb-24 sm:px-8 sm:pb-32">
        {failed && (
          <p role="alert" data-testid="events-error" className="rounded-xl bg-burgundy/20 px-5 py-4 text-sm text-ivory">
            We couldn't load the calendar just now. Please try again shortly.
          </p>
        )}

        {events && events.length === 0 && (
          <FadeUp>
            <div data-testid="events-empty" className="rounded-2xl border border-brass/15 bg-charcoal/50 p-10 sm:p-14">
              <p className="font-serif text-3xl italic text-ivory">
                The next game is still <span className="text-brass">being dealt.</span>
              </p>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-ivory/65">
                Join the Players List and you'll hear about upcoming gatherings before anyone else.
              </p>
              <Link
                to="/deal-me-in"
                data-testid="events-empty-deal-me-in-button"
                className="mt-7 inline-flex h-12 items-center rounded-xl bg-burgundy px-7 text-sm font-semibold text-ivory transition-colors hover:bg-[#a33847]"
              >
                Deal Me In
              </Link>
            </div>
          </FadeUp>
        )}

        {events && events.length > 0 && (
          <div className="space-y-6">
            {events.map((ev, i) => {
              const date = new Date(`${ev.event_date}T12:00:00`);
              const past = new Date(`${ev.event_date}T23:59:59`) < new Date();
              return (
                <FadeUp key={ev.id} delay={i * 0.06}>
                  <article
                    data-testid={`event-card-${ev.id}`}
                    className={`group grid gap-6 rounded-2xl border border-brass/15 bg-charcoal/50 p-7 transition-all duration-300 hover:border-brass/40 sm:grid-cols-[120px_1fr_auto] sm:items-center sm:p-9 ${
                      past ? "opacity-50" : ""
                    }`}
                  >
                    <div className="flex items-baseline gap-3 sm:block sm:text-center">
                      <span className="font-serif text-5xl text-brass sm:text-6xl">
                        {format(date, "d")}
                      </span>
                      <span className="block text-xs uppercase tracking-[0.25em] text-ivory/60 sm:mt-2">
                        {format(date, "MMM yyyy")}
                      </span>
                      <span className="text-xs text-ivory/40 sm:mt-1 sm:block">
                        {format(date, "EEEE")}
                      </span>
                    </div>
                    <div>
                      {past && (
                        <span className="mb-2 inline-block rounded-md border border-ivory/20 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-ivory/50">
                          Past gathering
                        </span>
                      )}
                      <h2 className="font-serif text-2xl text-ivory sm:text-3xl">{ev.name}</h2>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-brass">
                        {ev.theme}
                      </p>
                      <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-ivory/65">
                        {ev.descriptor}
                      </p>
                    </div>
                    <div className="sm:text-right">
                      {ev.ticket_enabled && ev.ticket_url && !past ? (
                        <a
                          href={ev.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`event-ticket-button-${ev.id}`}
                          className="inline-flex h-12 items-center gap-2 rounded-xl bg-burgundy px-6 text-sm font-semibold text-ivory transition-all duration-200 hover:bg-[#a33847] hover:shadow-[0_0_24px_rgba(140,46,59,0.45)]"
                        >
                          Claim a ticket
                          <ArrowUpRight size={15} />
                        </a>
                      ) : (
                        !past && (
                          <p className="max-w-[180px] text-xs leading-relaxed text-ivory/45 sm:ml-auto">
                            Tickets aren't released yet — join the Players List to hear first.
                          </p>
                        )
                      )}
                    </div>
                  </article>
                </FadeUp>
              );
            })}
          </div>
        )}

        <FadeUp delay={0.1}>
          <p className="mt-12 border-t border-brass/10 pt-8 text-sm leading-relaxed text-ivory/50">
            Players Lounge gatherings run for around three hours at private Sydney addresses.
            Confirmed Players receive venue details shortly before each event.{" "}
            <Link to="/deal-me-in" data-testid="events-footer-deal-me-in-link" className="text-brass underline underline-offset-4 hover:text-ivory">
              Join the Players List
            </Link>
            .
          </p>
        </FadeUp>
      </section>
    </div>
  );
};

export default Lounges;
